import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { C } from '../lib/theme.js';
import { Button, Card, ErrorLine, PageHeader, StatusPill } from '../ui/ui.js';
import { Icon } from '../ui/icons.js';
import { driveConfigured, pickDriveFolder } from '../lib/googleDrive.js';
import type {
  Connections as ConnectionsData,
  MetaSyncResult,
  CloudbedsSyncResult,
  MetaAdAccount,
} from '../lib/types.js';

function ConnCard({
  mark,
  markColor,
  title,
  status,
  children,
  footLeft,
  footRight,
  danger,
}: {
  mark: string;
  markColor: string;
  title: string;
  status: string;
  children: ReactNode;
  footLeft: ReactNode;
  footRight: ReactNode;
  danger?: boolean;
}) {
  return (
    <Card style={{ padding: 22, borderColor: danger ? 'rgba(224,115,106,.3)' : C.line }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: C.lineSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: `700 17px 'Outfit',sans-serif`,
            color: markColor,
          }}
        >
          {mark}
        </div>
        <StatusPill status={status} />
      </div>
      <div style={{ font: `600 15px 'Outfit',sans-serif`, marginBottom: 5, color: C.primary }}>
        {title}
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: C.muted,
          lineHeight: 1.55,
          marginBottom: 18,
          minHeight: 40,
        }}
      >
        {children}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
        }}
      >
        <span
          style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: danger ? C.act : C.faint }}
        >
          {footLeft}
        </span>
        <span style={{ fontSize: 12, color: danger ? C.act : C.secondary, cursor: 'pointer' }}>
          {footRight}
        </span>
      </div>
    </Card>
  );
}

/** Token-expiry countdown + <14-day warning for the Meta card. */
function TokenExpiry({ iso }: { iso?: string | null }) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  const days = Math.floor(ms / 86_400_000);
  const expired = ms <= 0;
  const warn = days < 14; // re-auth window
  const color = expired || warn ? C.act : C.good;
  const label = expired
    ? 'Access token expired — re-authorize in Meta'
    : warn
      ? `Access token expires in ${days}d — re-authorize soon`
      : `Access token valid · expires in ${days}d`;
  return (
    <div
      style={{
        marginTop: 10,
        font: `600 10.5px 'IBM Plex Mono',monospace`,
        color,
        letterSpacing: '.02em',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {(expired || warn) && <Icon name="alert" size={12} />}
      {label}
    </div>
  );
}

const metaLabel: CSSProperties = {
  display: 'grid',
  gap: 6,
  font: `500 10px 'IBM Plex Mono',monospace`,
  color: C.muted,
  letterSpacing: '.1em',
  textTransform: 'uppercase',
};
const metaInput: CSSProperties = {
  background: C.surface2,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '9px 11px',
  color: C.primary,
  font: `400 13px 'Inter',system-ui,sans-serif`,
};

export function Connections() {
  const { orgId, activeOrg } = useOrg();
  // Cloudbeds is a hotel-booking integration, relevant only to tenants that use it
  // (Phuket Cleanse). Hidden for other tenants (e.g. Propty).
  const usesCloudbeds = activeOrg?.slug === 'phuket-cleanse';
  const [data, setData] = useState<ConnectionsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [cbSyncing, setCbSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [confirmRemoveKey, setConfirmRemoveKey] = useState(false);
  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[] | null>(null);
  const [connectedAcct, setConnectedAcct] = useState('');
  const [linkingDrive, setLinkingDrive] = useState(false);
  const [confirmUnlinkDrive, setConfirmUnlinkDrive] = useState(false);
  const [confirmDisconnectGoogle, setConfirmDisconnectGoogle] = useState(false);
  // CAPI: Pixel/Dataset id + a manual push of bookings to Meta.
  const [pixelInput, setPixelInput] = useState<string | null>(null);
  const [savingPixel, setSavingPixel] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [capiMsg, setCapiMsg] = useState<string | null>(null);
  // Per-tenant Meta account/token/objective setup.
  const [tokenInput, setTokenInput] = useState('');
  const [savingToken, setSavingToken] = useState(false);
  const [replaceToken, setReplaceToken] = useState(false);
  const [acctInput, setAcctInput] = useState<string | null>(null);
  const [objInput, setObjInput] = useState<string | null>(null);
  const [savingAcct, setSavingAcct] = useState(false);
  const [metaSetupMsg, setMetaSetupMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    apiFetch<ConnectionsData>(`/api/orgs/${orgId}/connections`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
    apiFetch<{ accounts: MetaAdAccount[]; connected: string }>(`/api/orgs/${orgId}/meta/adaccounts`)
      .then((d) => {
        setAdAccounts(d.accounts);
        setConnectedAcct(d.connected);
      })
      .catch(() => setAdAccounts([]));
  }, [orgId]);

  useEffect(() => {
    setData(null);
    // Switching workspace must drop every unsaved input. These are local state, so
    // without this a value typed for one tenant stays in the form and can be saved
    // onto the next one — worst case pasting one tenant's Meta token, switching, and
    // writing it into another tenant's Vault entry.
    setPixelInput(null);
    setTokenInput('');
    setReplaceToken(false);
    setAcctInput(null);
    setObjInput(null);
    setCapiMsg(null);
    setMetaSetupMsg(null);
    setConfirmRemoveKey(false);
    load();
  }, [orgId, load]);

  // Surface the result of the Google OAuth round-trip (?drive=connected|error|…),
  // then scrub the query so a refresh doesn't repeat the banner.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get('drive');
    if (!status) return;
    setSyncMsg(
      status === 'connected'
        ? '✓ Google Drive connected — you won’t be asked to sign in again.'
        : status === 'cancelled'
          ? 'Google connection cancelled.'
          : 'Could not connect Google Drive. Please try again.',
    );
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  async function syncMeta() {
    setSyncing(true);
    setSyncMsg(null);
    setError(null);
    try {
      const r = await apiFetch<MetaSyncResult>(`/api/orgs/${orgId}/meta/sync`, { method: 'POST' });
      setSyncMsg(
        `Synced ${r.written} rows · ${r.currency ?? ''} · account ${r.account_status} · last ${r.days}d`,
      );
      load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function savePixel() {
    if (!orgId) return;
    setSavingPixel(true);
    setCapiMsg(null);
    try {
      const v = (pixelInput ?? data?.meta.pixel_id ?? '').trim();
      await apiFetch(`/api/orgs/${orgId}/meta/pixel`, {
        method: 'PUT',
        body: JSON.stringify({ pixel_id: v }),
      });
      setCapiMsg(
        v
          ? 'Pixel saved. Bookings will now push to Meta on each Cloudbeds sync.'
          : 'Pixel cleared.',
      );
      setPixelInput(null);
      load();
    } catch (e) {
      setCapiMsg(e instanceof Error ? e.message : 'Could not save pixel');
    } finally {
      setSavingPixel(false);
    }
  }

  async function pushCapi() {
    if (!orgId) return;
    setPushing(true);
    setCapiMsg(null);
    try {
      const r = await apiFetch<{ sent: number; stale: number; reason?: string }>(
        `/api/orgs/${orgId}/meta/capi/push`,
        { method: 'POST' },
      );
      setCapiMsg(
        r.reason
          ? r.reason
          : `Pushed ${r.sent} booking${r.sent === 1 ? '' : 's'} to Meta${
              r.stale ? ` · ${r.stale} too old to send (Meta's 7-day limit)` : ''
            }.`,
      );
    } catch (e) {
      setCapiMsg(e instanceof Error ? e.message : 'CAPI push failed');
    } finally {
      setPushing(false);
    }
  }

  async function syncCloudbeds() {
    setCbSyncing(true);
    setSyncMsg(null);
    setError(null);
    try {
      const r = await apiFetch<CloudbedsSyncResult>(`/api/orgs/${orgId}/cloudbeds/sync`, {
        method: 'POST',
      });
      setSyncMsg(
        `Synced ${r.written} reservations · property ${r.property_id} · ±${r.window_days}d`,
      );
      load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Cloudbeds sync failed');
    } finally {
      setCbSyncing(false);
    }
  }

  async function saveToken() {
    if (!orgId || tokenInput.trim().length < 20) return;
    setSavingToken(true);
    setMetaSetupMsg(null);
    try {
      const r = await apiFetch<{ token_last4: string }>(`/api/orgs/${orgId}/meta/token`, {
        method: 'PUT',
        body: JSON.stringify({ token: tokenInput.trim() }),
      });
      setTokenInput('');
      setReplaceToken(false);
      setMetaSetupMsg(`Meta token saved (••••${r.token_last4}).`);
      load();
    } catch (e) {
      setMetaSetupMsg(e instanceof Error ? e.message : 'Could not save the token');
    } finally {
      setSavingToken(false);
    }
  }

  async function saveAccount() {
    if (!orgId) return;
    setSavingAcct(true);
    setMetaSetupMsg(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/account`, {
        method: 'PUT',
        body: JSON.stringify({
          ad_account_id: (acctInput ?? data?.meta.ad_account_id ?? '').trim(),
          objective: objInput ?? data?.meta.objective ?? 'OUTCOME_TRAFFIC',
        }),
      });
      setAcctInput(null);
      setObjInput(null);
      setMetaSetupMsg('Ad account + objective saved.');
      load();
    } catch (e) {
      setMetaSetupMsg(e instanceof Error ? e.message : 'Could not save the account');
    } finally {
      setSavingAcct(false);
    }
  }

  async function saveKey() {
    if (!keyInput.trim()) return;
    setSavingKey(true);
    setSyncMsg(null);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/anthropic/key`, {
        method: 'PUT',
        body: JSON.stringify({ key: keyInput.trim() }),
      });
      setSyncMsg('Copy Engine key saved and verified.');
      setKeyInput('');
      setShowKey(false);
      load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Could not save key');
    } finally {
      setSavingKey(false);
    }
  }

  async function removeKey() {
    setSavingKey(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/anthropic/key`, { method: 'DELETE' });
      setSyncMsg('Copy Engine key removed.');
      load();
    } catch (e) {
      setSyncMsg(e instanceof Error ? e.message : 'Could not remove key');
    } finally {
      setSavingKey(false);
    }
  }

  async function linkDriveFolder() {
    setSyncMsg(null);
    setError(null);
    setLinkingDrive(true);
    try {
      const folder = await pickDriveFolder(orgId);
      if (!folder) return; // cancelled
      await apiFetch(`/api/orgs/${orgId}/drive/folder`, {
        method: 'PUT',
        body: JSON.stringify({
          folder_id: folder.id,
          folder_name: folder.name,
          folder_url: folder.url,
        }),
      });
      setSyncMsg(`Linked Google Drive folder “${folder.name}”.`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not link that folder');
    } finally {
      setLinkingDrive(false);
    }
  }

  async function unlinkDrive() {
    setConfirmUnlinkDrive(false);
    setSyncMsg(null);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/drive/folder`, { method: 'DELETE' });
      setSyncMsg('Google Drive folder unlinked.');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not unlink');
    }
  }

  // Eternal Drive access: kick off the server-side offline OAuth flow (full-page
  // redirect to Google). One grant, then the picker never re-prompts.
  async function connectGoogle() {
    setError(null);
    try {
      const { url } = await apiFetch<{ url: string }>(`/api/orgs/${orgId}/drive/oauth/start`);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start Google connection');
    }
  }
  async function disconnectGoogle() {
    setConfirmDisconnectGoogle(false);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/drive/oauth`, { method: 'DELETE' });
      setSyncMsg('Google account disconnected — the picker will ask again next time.');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not disconnect');
    }
  }

  const metaErr = data?.meta.status === 'error';
  const anthErr = data?.anthropic.status === 'error';
  const cbErr = data?.cloudbeds.status === 'error';
  const driveLinked = data?.drive?.status === 'connected' && !!data.drive.folder_id;

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1000, animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="Integrations · 4 services" title="Connections" />
      {error && <ErrorLine>{error}</ErrorLine>}
      {!data && !error && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          <ConnCard
            mark="M"
            markColor={C.info}
            title="Meta Ads"
            status={data.meta.status}
            danger={metaErr}
            footLeft={
              data.meta.account_status ? (
                <span style={{ color: C.basil }}>
                  {data.meta.ad_account_id ?? ''} · {data.meta.account_status}
                </span>
              ) : (
                (data.meta.ad_account_id ?? 'NOT LINKED')
              )
            }
            footRight={
              <span
                onClick={() => !syncing && void syncMeta()}
                style={{
                  cursor: syncing ? 'default' : 'pointer',
                  color: syncing ? C.muted : C.basil,
                }}
              >
                {syncing ? 'Syncing…' : '↻ Sync now'}
              </span>
            }
          >
            Builds and reads campaigns on the connected account — spend, reach, CTR/CPC, Hook/Hold
            rates, plus organic publishing and amplify. Every campaign is built paused.
            <TokenExpiry iso={data.meta.token_expires_at} />
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: `1px solid ${C.lineSoft}`,
              }}
            >
              <div
                style={{
                  font: `600 10px 'IBM Plex Mono',monospace`,
                  letterSpacing: '.08em',
                  color: C.muted,
                  marginBottom: 6,
                }}
              >
                {usesCloudbeds ? 'CONVERSIONS API (CAPI) — SEND BOOKINGS TO META' : 'META PIXEL'}
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                {usesCloudbeds ? (
                  <>
                    Feeds real bookings back to Meta so it optimizes for revenue, not just clicks.
                    Uses the hashed guest email already stored (raw PII never leaves). Paste your
                    Meta <strong>Pixel / Dataset ID</strong> (Events Manager → Data sources).
                  </>
                ) : (
                  <>
                    The dataset Meta optimizes against. Required for the Leads objective — campaigns
                    bid toward this pixel&rsquo;s conversion event. Paste your Meta{' '}
                    <strong>Pixel / Dataset ID</strong> (Events Manager → Data sources).
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  value={pixelInput ?? data.meta.pixel_id ?? ''}
                  onChange={(e) => setPixelInput(e.target.value)}
                  placeholder="e.g. 1234567890123456"
                  style={{
                    flex: 1,
                    minWidth: 180,
                    boxSizing: 'border-box',
                    background: C.surface2,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    padding: '8px 11px',
                    fontSize: 13,
                    color: C.primary,
                    fontFamily: 'inherit',
                  }}
                />
                <span
                  onClick={() => !savingPixel && void savePixel()}
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: C.basil,
                    cursor: savingPixel ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {savingPixel ? 'Saving…' : 'Save'}
                </span>
                {usesCloudbeds && data.meta.pixel_id && (
                  <span
                    onClick={() => !pushing && void pushCapi()}
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: pushing ? C.muted : C.secondary,
                      cursor: pushing ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pushing ? 'Pushing…' : '↗ Push bookings now'}
                  </span>
                )}
              </div>
              {capiMsg && (
                <div style={{ fontSize: 11.5, color: C.secondary, marginTop: 8, lineHeight: 1.5 }}>
                  {capiMsg}
                </div>
              )}
            </div>
          </ConnCard>

          {/* Per-tenant Meta setup: this workspace's own ad account, objective + token. */}
          <Card style={{ padding: 22, gridColumn: '1 / -1' }}>
            <div
              style={{ font: `600 15px 'Outfit',sans-serif`, marginBottom: 4, color: C.primary }}
            >
              Meta account &amp; token
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 18 }}>
              This workspace&rsquo;s own Meta ad account, campaign objective, and System User token.
              Leave blank to inherit the platform default.
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr',
                gap: 14,
                marginBottom: 14,
              }}
            >
              <label style={metaLabel}>
                Ad account ID
                <input
                  style={metaInput}
                  placeholder="act_..."
                  value={acctInput ?? data.meta.ad_account_id ?? ''}
                  onChange={(e) => setAcctInput(e.target.value)}
                />
              </label>
              <label style={metaLabel}>
                Objective
                <select
                  style={metaInput}
                  value={objInput ?? data.meta.objective ?? 'OUTCOME_TRAFFIC'}
                  onChange={(e) => setObjInput(e.target.value)}
                >
                  <option value="OUTCOME_TRAFFIC">Traffic (to a landing page)</option>
                  <option value="OUTCOME_LEADS">Leads (needs a pixel)</option>
                </select>
              </label>
            </div>
            <div style={{ marginBottom: 18 }}>
              <Button variant="ghost" onClick={saveAccount} disabled={savingAcct}>
                {savingAcct ? 'Saving…' : 'Save account + objective'}
              </Button>
            </div>

            <label style={metaLabel}>
              System User token
              {data.meta.token_set_at && !replaceToken ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 13,
                    color: C.secondary,
                  }}
                >
                  <span style={{ color: C.good }}>
                    Connected · ••••{data.meta.token_last4 ?? '????'}
                  </span>
                  <span
                    onClick={() => setReplaceToken(true)}
                    style={{ cursor: 'pointer', color: C.muted, fontSize: 12 }}
                  >
                    Replace
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    style={{ ...metaInput, flex: 1 }}
                    type="password"
                    placeholder="Paste the Meta System User token"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                  />
                  <Button
                    variant="primary"
                    onClick={saveToken}
                    disabled={savingToken || tokenInput.trim().length < 20}
                  >
                    {savingToken ? 'Saving…' : 'Save token'}
                  </Button>
                </div>
              )}
            </label>
            {metaSetupMsg && (
              <div style={{ marginTop: 12, fontSize: 12, color: C.secondary }}>{metaSetupMsg}</div>
            )}
          </Card>

          <ConnCard
            mark="✦"
            markColor={C.basil}
            title="Copy Engine"
            status={data.anthropic.status}
            danger={anthErr}
            footLeft={
              data.anthropic.key_last4 ? (
                <span style={{ color: C.basil }}>sk-ant-…{data.anthropic.key_last4}</span>
              ) : (
                'NO KEY'
              )
            }
            footRight={
              data.anthropic.key_last4 ? (
                confirmRemoveKey ? (
                  <span>
                    <span
                      onClick={() => {
                        if (!savingKey) void removeKey();
                        setConfirmRemoveKey(false);
                      }}
                      style={{ color: C.act, fontWeight: 600 }}
                    >
                      Remove?
                    </span>
                    <span
                      onClick={() => setConfirmRemoveKey(false)}
                      style={{ color: C.muted, marginLeft: 8 }}
                    >
                      Cancel
                    </span>
                  </span>
                ) : (
                  <span onClick={() => setConfirmRemoveKey(true)} style={{ color: C.act }}>
                    Remove
                  </span>
                )
              ) : (
                <span onClick={() => setShowKey((s) => !s)} style={{ color: C.basil }}>
                  {showKey ? 'Cancel' : 'Add key →'}
                </span>
              )
            }
          >
            This workspace&rsquo;s own Anthropic API key (encrypted in Supabase Vault). Powers
            on-brand copy generation; each tenant bills its own key.
            {showKey && !data.anthropic.key_last4 && (
              <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
                <input
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="sk-ant-…"
                  type="password"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: C.surface2,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    padding: '7px 10px',
                    fontSize: 12,
                    color: C.primary,
                    fontFamily: 'IBM Plex Mono, monospace',
                  }}
                />
                <span
                  onClick={() => !savingKey && void saveKey()}
                  style={{
                    fontSize: 12,
                    color: savingKey ? C.muted : C.basil,
                    cursor: savingKey ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                    alignSelf: 'center',
                  }}
                >
                  {savingKey ? 'Verifying…' : 'Save'}
                </span>
              </div>
            )}
          </ConnCard>

          {usesCloudbeds && (
            <ConnCard
              mark="C"
              markColor={C.energy}
              title="Cloudbeds"
              status={data.cloudbeds.status}
              danger={cbErr}
              footLeft={
                data.cloudbeds.property_id ? (
                  <span style={{ color: C.energy }}>
                    {String(data.cloudbeds.property_id)} · {data.cloudbeds.reservation_count ?? 0}{' '}
                    res
                  </span>
                ) : (
                  'NOT LINKED'
                )
              }
              footRight={
                <span
                  onClick={() => !cbSyncing && void syncCloudbeds()}
                  style={{
                    cursor: cbSyncing ? 'default' : 'pointer',
                    color: cbSyncing ? C.muted : C.energy,
                  }}
                >
                  {cbSyncing ? 'Syncing…' : '↻ Sync now'}
                </span>
              }
            >
              Reservation sync for real booking attribution (closes the clicks→bookings gap). Pulls
              bookings via the Cloudbeds API key; ROAS-by-campaign attribution lands next.
            </ConnCard>
          )}

          <ConnCard
            mark="▲"
            markColor={C.good}
            title="Google Drive"
            status={driveLinked ? 'connected' : 'not_connected'}
            footLeft={
              driveLinked ? (
                <a
                  href={data.drive?.folder_url ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: C.good, textDecoration: 'none' }}
                >
                  {data.drive?.folder_name ?? 'Linked folder'}
                </a>
              ) : (
                'NOT LINKED'
              )
            }
            footRight={
              !driveConfigured ? (
                <span style={{ color: C.faint }}>Not configured</span>
              ) : driveLinked ? (
                confirmUnlinkDrive ? (
                  <span>
                    <span
                      onClick={() => void unlinkDrive()}
                      style={{ color: C.act, fontWeight: 600 }}
                    >
                      Unlink?
                    </span>
                    <span
                      onClick={() => setConfirmUnlinkDrive(false)}
                      style={{ color: C.muted, marginLeft: 8 }}
                    >
                      Cancel
                    </span>
                  </span>
                ) : (
                  <span
                    onClick={() => !linkingDrive && void linkDriveFolder()}
                    style={{ color: linkingDrive ? C.muted : C.basil, cursor: 'pointer' }}
                  >
                    {linkingDrive ? 'Opening…' : 'Change →'}
                  </span>
                )
              ) : (
                <span
                  onClick={() => !linkingDrive && void linkDriveFolder()}
                  style={{ color: linkingDrive ? C.muted : C.basil, cursor: 'pointer' }}
                >
                  {linkingDrive ? 'Opening…' : 'Link folder →'}
                </span>
              )
            }
          >
            Link one Drive folder as your asset source. The media pickers (Create, Library) open
            straight into it, and the link stays put across logins. Files import only when you pick
            them — nothing syncs on its own.
            {driveConfigured && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: `1px solid ${C.lineSoft}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
                  SIGN-IN
                </span>
                {data.drive?.oauth_connected ? (
                  <>
                    <span style={{ fontSize: 11.5, color: C.good }}>
                      ✓ Connected{data.drive.google_email ? ` · ${data.drive.google_email}` : ''} —
                      never re-prompts
                    </span>
                    {confirmDisconnectGoogle ? (
                      <span style={{ fontSize: 11.5, display: 'flex', gap: 8 }}>
                        <span
                          onClick={() => void disconnectGoogle()}
                          style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Disconnect?
                        </span>
                        <span
                          onClick={() => setConfirmDisconnectGoogle(false)}
                          style={{ color: C.muted, cursor: 'pointer' }}
                        >
                          Cancel
                        </span>
                      </span>
                    ) : (
                      <span
                        onClick={() => setConfirmDisconnectGoogle(true)}
                        style={{ fontSize: 11.5, color: C.faint, cursor: 'pointer' }}
                      >
                        Disconnect
                      </span>
                    )}
                  </>
                ) : (
                  <span
                    onClick={() => void connectGoogle()}
                    title="Connect once — the picker will never ask you to sign in again"
                    style={{ fontSize: 11.5, color: C.basil, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Connect Google (never expires) →
                  </span>
                )}
              </div>
            )}
            {driveLinked && (
              <div style={{ marginTop: 10 }}>
                <span
                  onClick={() => !confirmUnlinkDrive && setConfirmUnlinkDrive(true)}
                  style={{
                    font: `500 11px 'IBM Plex Mono',monospace`,
                    color: C.act,
                    cursor: 'pointer',
                  }}
                >
                  Unlink folder
                </span>
              </div>
            )}
          </ConnCard>
        </div>
      )}

      {syncMsg && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 10,
            background: C.surface2,
            border: `1px solid ${C.line}`,
            font: `500 12px 'IBM Plex Mono',monospace`,
            color: C.secondary,
          }}
        >
          {syncMsg}
        </div>
      )}

      {/* Every ad account the token can see — find where phone/app 'Boost' ads live */}
      {adAccounts && adAccounts.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div
            style={{
              font: `500 10px 'IBM Plex Mono',monospace`,
              letterSpacing: '.12em',
              color: C.faint,
              marginBottom: 4,
            }}
          >
            AD ACCOUNTS THIS TOKEN CAN SEE
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
            Basil reads campaigns from the account marked <b>Connected</b>. If a boosted post is
            missing from Campaigns, it was likely promoted into one of the other accounts below.
          </div>
          <Card style={{ overflow: 'hidden' }}>
            {adAccounts.map((a, i) => {
              const isConn = a.id === connectedAcct || a.id === `act_${connectedAcct}`;
              return (
                <div
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 16px',
                    borderTop: i === 0 ? 'none' : `1px solid ${C.lineSoft}`,
                  }}
                >
                  <span style={{ fontSize: 13, color: C.primary, fontWeight: 500 }}>{a.name}</span>
                  <span style={{ font: `500 11px 'IBM Plex Mono',monospace`, color: C.faint }}>
                    {a.id}
                  </span>
                  {a.currency && (
                    <span style={{ fontSize: 11.5, color: C.muted }}>· {a.currency}</span>
                  )}
                  <span style={{ marginLeft: 'auto' }}>
                    {isConn ? (
                      <StatusPill status="active" label="CONNECTED" />
                    ) : (
                      <span style={{ fontSize: 11.5, color: C.faint }}>not read by Basil</span>
                    )}
                  </span>
                </div>
              );
            })}
          </Card>
        </div>
      )}
    </div>
  );
}
